import React, { useEffect, useState, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { FaSpinner, FaSortAmountDown, FaSortAmountUp } from "react-icons/fa";
import { useLanguage } from "../components/LanguajeContext";
import { translations } from "../utils/reservationOperator.translate";
// Lazy load de iconos
const FaCalendarAlt = React.lazy(() => import("react-icons/fa").then(mod => ({ default: mod.FaCalendarAlt })));
const FaUsers = React.lazy(() => import("react-icons/fa").then(mod => ({ default: mod.FaUsers })));
const FaPhone = React.lazy(() => import("react-icons/fa").then(mod => ({ default: mod.FaPhone })));
const FaMoneyBillWave = React.lazy(() => import("react-icons/fa").then(mod => ({ default: mod.FaMoneyBillWave })));

const API_BASE_URL = process.env.REACT_APP_SERVER_URL;

const OperatorReservations = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("date");
  const [sortDirection, setSortDirection] = useState("asc");
  const [serviceFilter, setServiceFilter] = useState("");

  // Obtener el lenguaje del contexto
  const { language } = useLanguage() || { language: 'es' };
  const t = translations[language] || translations.es;

  useEffect(() => {
    const fetchReservations = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const id_operator = localStorage.getItem("id_operator");
       // const authToken = localStorage.getItem("token");

        if (!id_operator) {
          throw new Error(t.noCredentials);
        }

        const response = await fetch(
          `${API_BASE_URL}/api/booking/test/${id_operator}`,
          {
            headers: {
              "Content-Type": "application/json",
              //"Authorization": `Bearer ${authToken}`
            },
            credentials:"include"

          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `${t.error} ${response.status}`);
        }

        const data = await response.json();
        setReservations(data.message || []);

      } catch (error) {
        console.error("Error:", error);
        setError(error.message);
        
        if (error.message.includes("autenticación") || error.message.includes("401")) {
          navigate("/login");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReservations();
  }, [navigate, language]); // Agregar language como dependencia

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === 'es' ? "es-CR" : "en-US", {
      style: "currency",
      currency: "CRC",
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return new Date(dateString).toLocaleDateString(language === 'es' ? "es-ES" : "en-US", options);
  };

  const handleSortChange = (e) => setSortBy(e.target.value);
  const toggleSortDirection = () => setSortDirection(prev => prev === "asc" ? "desc" : "asc");

  const getFilteredAndSortedReservations = () => {
    let filtered = [...reservations];

    if (serviceFilter) {
      filtered = filtered.filter(reservation => 
        reservation.service?.name?.toLowerCase().includes(serviceFilter.toLowerCase())
      );
    }

    filtered.sort((a, b) => {
      if (sortBy === "date") {
        const dateA = new Date(a.date_service_booking);
        const dateB = new Date(b.date_service_booking);
        return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
      } else {
        const nameA = a.service?.name?.toLowerCase() || '';
        const nameB = b.service?.name?.toLowerCase() || '';
        if (nameA < nameB) return sortDirection === "asc" ? -1 : 1;
        if (nameA > nameB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      }
    });

    return filtered;
  };

  const filteredAndSortedReservations = getFilteredAndSortedReservations();

  return (
    <div className="min-h-screen bg-[#102B3F] text-white p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold mb-6 text-green-300">{t.myReservations}</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <div className="bg-[#2D4A5E] p-4 rounded-xl border border-[#335066]">
            <label htmlFor="serviceFilter" className="block text-sm font-medium text-gray-300 mb-2">
              {t.filterByService}
            </label>
            <input
              type="text"
              id="serviceFilter"
              className="w-full px-4 py-2 bg-[#1B3A4B] border border-[#335066] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-300"
              placeholder={t.servicePlaceholder}
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
            />
          </div>
          
          <div className="bg-[#2D4A5E] p-4 rounded-xl border border-[#335066]">
            <label htmlFor="sortBy" className="block text-sm font-medium text-gray-300 mb-2">
              {t.sortBy}
            </label>
            <div className="flex gap-2">
              <select
                id="sortBy"
                className="flex-1 px-4 py-2 bg-[#1B3A4B] border border-[#335066] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-300"
                value={sortBy}
                onChange={handleSortChange}
              >
                <option value="date">{t.date}</option>
                <option value="service">{t.serviceName}</option>
              </select>
              
              <button
                onClick={toggleSortDirection}
                className="px-4 py-2 bg-[#1B3A4B] hover:bg-[#335066] text-white rounded-lg border border-[#335066] flex items-center gap-2 transition-colors"
                title={sortDirection === "asc" ? t.ascending : t.descending}
              >
                <Suspense fallback={<FaSpinner className="animate-spin" />}>
                  {sortDirection === "asc" ? <FaSortAmountDown /> : <FaSortAmountUp />}
                </Suspense>
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-300">
            <FaSpinner className="animate-spin text-4xl mb-4" />
            <p>{t.loadingReservations}</p>
          </div>
        ) : error ? (
          <div className="bg-[#2D4A5E] border-l-4 border-red-500 p-4 rounded-lg mb-6">
            <p className="text-red-400 font-medium">{t.error}: {error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-3 px-4 py-2 bg-red-500 hover:bg-red-400 text-white rounded-lg transition-colors"
            >
              {t.retry}
            </button>
          </div>
        ) : filteredAndSortedReservations.length === 0 ? (
          <div className="bg-[#2D4A5E] border-l-4 border-blue-400 p-4 rounded-lg">
            <p className="text-blue-300">{t.noReservations}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSortedReservations.map((reservation) => (
              <div key={reservation.id} className="bg-[#2D4A5E] p-6 rounded-2xl shadow-lg border border-[#335066] hover:scale-[1.02] transition-transform duration-200">
                <h3 className="text-xl font-bold text-green-300 mb-3">{reservation.service?.name || t.serviceNotAvailable}</h3>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Suspense fallback={<FaSpinner className="animate-spin mt-1" />}>
                      <FaCalendarAlt className="mt-1 text-gray-400" />
                    </Suspense>
                    <div>
                      <p className="text-gray-400 text-sm">{t.date}</p>
                      <p className="text-white">{formatDate(reservation.date_service_booking)}</p>
                      <p className="text-gray-300">{reservation.hour_start} - {reservation.hour_end}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Suspense fallback={<FaSpinner className="animate-spin mt-1" />}>
                      <FaUsers className="mt-1 text-gray-400" />
                    </Suspense>
                    <div>
                      <p className="text-gray-400 text-sm">{t.client}</p>
                      <p className="text-white">{reservation.client_name}</p>
                    </div>
                  </div>
                   <div className="flex items-start gap-3">
                    <Suspense fallback={<FaSpinner className="animate-spin mt-1" />}>
                      <FaUsers className="mt-1 text-gray-400" />
                    </Suspense>
                    <div>
                      <p className="text-gray-400 text-sm">{t.client_email}</p>
                      <p className="text-white">{reservation.client_email}</p>
                    </div>
                  </div>
                  
                {reservation.client_phone && 
                
                 <div className="flex items-start gap-3">
                    <Suspense fallback={<FaSpinner className="animate-spin mt-1" />}>
                      <FaPhone className="mt-1 text-gray-400" />
                    </Suspense>
                    <div>
                      <p className="text-gray-400 text-sm">{t.contact}</p>
                      <p className="text-white">{reservation.client_phone}</p>
                    </div>
                  </div>
                  }

                 
                  
                  <div className="flex items-start gap-3">
                    <Suspense fallback={<FaSpinner className="animate-spin mt-1" />}>
                      <FaMoneyBillWave className="mt-1 text-gray-400" />
                    </Suspense>
                    <div>
                      <p className="text-gray-400 text-sm">{t.total}</p>
                      <p className="text-white">{formatCurrency(reservation.total_amount)}</p>
                      <p className="text-gray-300 text-sm">{reservation.participants} {t.participants}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OperatorReservations;